import { API } from '../Api';

// "Phát nhạc theo yêu cầu" (kiểu zypage.com/dhdota): người xem trả Ca kèm link YouTube,
// streamer duyệt (mặc định) hoặc để tự phát, bài được phát trên overlay livestream.
// Thu Ca đi lại đường DonateRequest có sẵn (min/max donate, chặn tự donate, lịch sử donate).

export type MusicMode = 'Approve' | 'Auto';
export type MusicStatus =
  | 'Pending'
  | 'Approving'
  | 'Queued'
  | 'Playing'
  | 'Played'
  | 'Skipped'
  | 'Rejected'
  | 'Failed'
  | 'Expired';

export interface MusicTier {
  price: number;
  seconds: number;
}

export interface MusicItem {
  id: string;
  videoId: string;
  title: string;
  author: string | null;
  thumbnailUrl: string | null;
  requesterName: string;
  message: string | null;
  isAnonymous: boolean;
  price: number;
  /** Thời lượng được phát đã chốt lúc gửi. Máy chủ cũ không trả ⇒ dùng maxSeconds của kênh. */
  seconds?: number | null;
  status: MusicStatus;
  createdAt: string;
  queuedAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
  failReason: string | null;
  /** Chỉ có khi status là Queued: thứ tự trong hàng chờ chung của kênh (1 = phát tiếp theo). */
  position?: number | null;
  /** Chỉ có khi status là Queued: ước tính số giây nữa tới lượt phát (cận trên). */
  etaSeconds?: number | null;
}

export interface MusicConfig {
  isEnabled: boolean;
  price: number;
  mode: MusicMode;
  maxSeconds: number;
  tiers?: MusicTier[];
  maxPendingPerViewer: number;
  isPaused: boolean;
  overlayKey: string;
  /** Âm lượng nhạc trên OBS, 0–100. Máy chủ cũ không trả ⇒ coi như 100. */
  volume?: number;
}

export interface MusicPublicInfo {
  enabled: boolean;
  price: number;
  mode: MusicMode;
  maxSeconds: number;
  tiers?: MusicTier[];
}

export interface MusicPreview {
  videoId: string;
  title: string;
  author: string | null;
  thumbnailUrl: string | null;
}

export interface MusicQueue {
  config: MusicConfig;
  pending: MusicItem[];
  playing: MusicItem | null;
  queued: MusicItem[];
  history: MusicItem[];
  /** Có lớp phủ nhạc đang giữ quyền phát (gọi máy chủ trong 20 giây qua). */
  overlayActive?: boolean;
  /** Có lớp phủ thứ hai bị từ chối vì đã có lớp phủ khác đang giữ quyền. */
  overlayActiveElsewhere?: boolean;
}

export interface MusicOverlay {
  isPaused: boolean;
  maxSeconds: number;
  /** Âm lượng streamer đặt, 0–100. Máy chủ cũ không trả ⇒ 100. */
  volume?: number;
  current: MusicItem | null;
  next: MusicItem[];
  /** Giờ máy chủ (UTC) lúc dựng phản hồi, dùng để tính số giây đã phát thay cho đồng hồ OBS. */
  serverNow?: string;
  /** false ⇒ lớp phủ này không giữ quyền phát (current rỗng, next rỗng). Máy chủ cũ không trả. */
  isActiveInstance?: boolean;
}

export interface SubmitMusicBody {
  creatorId: string;
  url: string;
  message?: string | null;
  isAnonymous: boolean;
  /** Giá của mức đã chọn. Bỏ trống ⇒ máy chủ lấy mức thấp nhất. */
  tierPrice?: number;
  /** Thời lượng của mức đã chọn, gửi kèm tierPrice để máy chủ đối chiếu. */
  tierSeconds?: number;
}

export interface SaveMusicConfigBody {
  isEnabled: boolean;
  price: number;
  mode: MusicMode;
  maxSeconds: number;
  tiers: MusicTier[];
  maxPendingPerViewer: number;
  /** Bỏ trống ⇒ máy chủ giữ nguyên âm lượng đang lưu. */
  volume?: number;
}

const BASE = '/v1/payment-service/music-request';
const PUBLIC_BASE = '/v1/payment-service/public/music-request';

// ── Công khai (không cần đăng nhập) ─────────────────────────────────────────────────────

export const getMusicInfo = (userId: string) =>
  API.get<MusicPublicInfo>(`${PUBLIC_BASE}/info`, { params: { userId } }).then((r) => r.data);

export const previewMusic = (url: string) =>
  API.get<MusicPreview>(`${PUBLIC_BASE}/preview`, { params: { url } }).then((r) => r.data);

export const getMusicOverlay = (userId: string, key: string, instanceId?: string, previousInstanceId?: string) =>
  API.get<MusicOverlay>(`${PUBLIC_BASE}/overlay`, { params: { userId, key, instanceId, previousInstanceId } }).then(
    (r) => r.data
  );

export const overlayStarted = (userId: string, key: string, id: string, instanceId?: string, previousInstanceId?: string) =>
  API.post<MusicOverlay>(
    `${PUBLIC_BASE}/overlay/started`,
    {},
    { params: { userId, key, id, instanceId, previousInstanceId } }
  ).then((r) => r.data);

export const overlayEnded = (
  userId: string,
  key: string,
  id: string,
  reason: string,
  instanceId?: string,
  previousInstanceId?: string
) =>
  API.post<MusicOverlay>(
    `${PUBLIC_BASE}/overlay/ended`,
    {},
    { params: { userId, key, id, reason, instanceId, previousInstanceId } }
  ).then((r) => r.data);

// ── Người xem (đăng nhập) ───────────────────────────────────────────────────────────────

export const submitMusic = (body: SubmitMusicBody) =>
  API.post<MusicItem>(BASE, body).then((r) => r.data);

export const getMyMusic = (creatorId: string) =>
  API.get<MusicItem[]>(`${BASE}/mine`, { params: { creatorId } }).then((r) => r.data);

// ── Streamer (đăng nhập, chủ kênh) ──────────────────────────────────────────────────────

export const getMusicConfig = () => API.get<MusicConfig>(`${BASE}/manage/config`).then((r) => r.data);

export const saveMusicConfig = (body: SaveMusicConfigBody) =>
  API.post<MusicConfig>(`${BASE}/manage/config`, body).then((r) => r.data);

export const regenerateMusicKey = () =>
  API.post<MusicConfig>(`${BASE}/manage/config/regenerate-key`).then((r) => r.data);

export const getMusicQueue = () => API.get<MusicQueue>(`${BASE}/manage/queue`).then((r) => r.data);

export const approveMusic = (id: string) =>
  API.post<MusicItem>(`${BASE}/manage/${id}/approve`).then((r) => r.data);

export const rejectMusic = (id: string) =>
  API.post<MusicItem>(`${BASE}/manage/${id}/reject`).then((r) => r.data);

export const skipMusic = () => API.post<MusicItem | null>(`${BASE}/manage/skip`).then((r) => r.data);

export const pauseMusic = () => API.post<MusicConfig>(`${BASE}/manage/pause`).then((r) => r.data);

export const resumeMusic = () => API.post<MusicConfig>(`${BASE}/manage/resume`).then((r) => r.data);

/** Đổi âm lượng nhạc trên OBS ngay (0–100); lớp phủ đang mở nhận tin qua hub và đổi theo. */
export const setMusicVolume = (volume: number) =>
  API.post<MusicConfig>(`${BASE}/manage/volume`, { volume: Math.round(volume) }).then((r) => r.data);
